// ============================================
// Dashboard - 数据看板组件
// 完整生产级实现
// ============================================

import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface HealthData {
  status: string
  timestamp: string
  latency: {
    total: number
    database: number
    kv: number
  }
  components: {
    database: { status: string; latency: number }
    kv: { status: string; latency: number }
    llm: {
      doubao: { status: string; failureCount: number }
      deepseek: { status: string; failureCount: number }
    }
  }
  metrics: {
    tasks: { pending: number; running: number; failed: number }
    data: { totalBids: number; totalAgentLogs: number }
  }
}

interface Props {
  health: HealthData | null
}

export default function Dashboard({ health }: Props) {
  const [stats, setStats] = useState<any>(null)

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/statistics')
      const data = await response.json()
      if (data.success) {
        setStats(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error)
    }
  }

  if (!health || !stats) {
    return <div className="text-white">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 系统状态概览 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">系统状态</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {health.status === 'healthy' ? '正常' : '异常'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              延迟: {health.latency.total}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">数据库</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${health.components.database.status === 'operational' ? 'text-green-400' : 'text-red-400'}`}>
              {health.components.database.status === 'operational' ? '正常' : '异常'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              延迟: {health.components.database.latency}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">KV存储</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${health.components.kv.status === 'operational' ? 'text-green-400' : 'text-red-400'}`}>
              {health.components.kv.status === 'operational' ? '正常' : '异常'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              延迟: {health.components.kv.latency}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">LLM服务</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${health.components.llm.doubao.status === 'available' || health.components.llm.deepseek.status === 'available' ? 'text-green-400' : 'text-red-400'}`}>
              {health.components.llm.doubao.status === 'available' || health.components.llm.deepseek.status === 'available' ? '可用' : '不可用'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              豆包: {health.components.llm.doubao.status} | DeepSeek: {health.components.llm.deepseek.status}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 数据统计 */}
      <Card>
        <CardHeader>
          <CardTitle>数据统计</CardTitle>
          <CardDescription>金融AI招投标数据概览</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div>
              <div className="text-sm text-slate-400">总项目数</div>
              <div className="text-2xl font-bold text-white mt-1">{stats.totalProjects?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">总金额（万元）</div>
              <div className="text-2xl font-bold text-white mt-1">{stats.totalAmount?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">同比增长（项目）</div>
              <div className="text-2xl font-bold text-green-400 mt-1">+{stats.yearOverYearGrowth?.projects}%</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">同比增长（金额）</div>
              <div className="text-2xl font-bold text-green-400 mt-1">+{stats.yearOverYearGrowth?.amount}%</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">智能体项目</div>
              <div className="text-2xl font-bold text-white mt-1">{stats.agentProjectsCount?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">RaaS项目</div>
              <div className="text-2xl font-bold text-white mt-1">{stats.raasProjectsCount?.toLocaleString()}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 任务统计 */}
      <Card>
        <CardHeader>
          <CardTitle>任务统计</CardTitle>
          <CardDescription>当前任务队列状态</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-l-4 border-yellow-500 pl-4">
              <div className="text-sm text-slate-400">待处理</div>
              <div className="text-3xl font-bold text-white mt-1">{health.metrics.tasks.pending}</div>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <div className="text-sm text-slate-400">执行中</div>
              <div className="text-3xl font-bold text-white mt-1">{health.metrics.tasks.running}</div>
            </div>
            <div className="border-l-4 border-red-500 pl-4">
              <div className="text-sm text-slate-400">失败</div>
              <div className="text-3xl font-bold text-white mt-1">{health.metrics.tasks.failed}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 服务商排行 */}
      <Card>
        <CardHeader>
          <CardTitle>五强服务商排行</CardTitle>
          <CardDescription>按中标频次和金额统计</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.topProviders?.map((provider: any, index: number) => (
              <div key={index} className="flex items-center justify-between border-b border-slate-700 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-white">{provider.name}</div>
                    <div className="text-sm text-slate-400">
                      {provider.projectCount} 个项目
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-white">
                    ¥{(provider.amount / 10000).toFixed(2)}亿
                  </div>
                  <div className="text-sm text-slate-400">
                    {provider.scenarios?.slice(0, 2).join(', ')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 刷新按钮 */}
      <div className="flex justify-end">
        <Button onClick={fetchStatistics}>刷新数据</Button>
      </div>
    </div>
  )
}
